import $ from 'jquery';
import Flickity from 'flickity';
import EventHandler from '@pixelunion/events';
import { selectors } from '../Constants';

export default class DynamicSlideshow {
  constructor(section) {
    this.$el = $(section.el);
    this.$body = $(document.body);
    this.data = section.data;

    this.$slideshow = this.$el.find(selectors.slideshow.container);
    this.$slides = this.$el.find(selectors.slideshow.slide);
    this.$selectedSlide = $('.slideshow-slide.is-selected', this.$el);

    this._setCellHeights = this._setCellHeights.bind(this);
    this._onSlideSettle = this._onSlideSettle.bind(this);
    this.selectSlide = this.selectSlide.bind(this);

    this.pendingSlideIndex = 0;
    this.flickity = null;
    this.events = new EventHandler();
    this.autoplay = this.data.autoplay
      ? this.data.autoplay * 1000
      : this.data.autoplay;

    // Only start Slideshow if container exists
    if (!this.$slideshow.length) {
      return;
    }

    this.events.register(this.$el[0], 'rimg:load', this._setCellHeights);
    this.events.register(window, 'resize', this._setCellHeights);

    // Images first load with an svg data uri for the srcset. Normally this means loading
    // is instant and images have dimensions by this point in the code, but Safari has a
    // weird delay so if we initialize Flickity too soon, we end up with a 0 height slideshow
    // until the real image loads and the rimg:load events triggers another height update.
    // This issue doesn't affect true svg element (ie placeholder images).
    const firstImage = this.$slides[0].querySelector('img');
    if (!firstImage || firstImage.naturalHeight > 0) {
      this._initFlickity();
    } else {
      this.events.register(firstImage, 'load', () => this._initFlickity());
    }
  }

  onSectionBlockSelect(block) {
    const slideIndex = this._getSlideIndex(block.el);

    if (!this.flickity) {
      this.pendingSlideIndex = slideIndex;
      return;
    }

    this.selectSlide(slideIndex, true, false);

    if (this.autoplay) {
      this.flickity.stopPlayer();
    }
  }

  onSectionBlockDeselect() {
    this.pendingSlideIndex = 0;

    if (!this.flickity) {
      return;
    }

    if (this.autoplay) {
      this.flickity.playPlayer();
    }
  }

  onSectionUnload() {
    this.events.unregisterAll();

    if (this.flickity) {
      this.flickity.destroy();
    }
  }

  /**
   *  Move to the active slide
   * @param slideIndex
   * @param isWrap
   * @param isInstant
   */
  selectSlide(slideIndex, isWrap = false, isInstant = false) {
    if (slideIndex === this.flickity.selectedIndex) {
      return;
    }

    this.flickity.select(slideIndex, isWrap, isInstant);

    const cell = this.$slides[slideIndex];
    const $focusable = $(cell)
      .find(`${selectors.slideshow.pagination}, ${selectors.slideshow.linkButton}`)
      .eq(0);
    $focusable.focus();
  }

  /**
   * Get the index of the slide from the element
   *
   * @param el
   * @returns {Number}
   * @private
   */
  _getSlideIndex(el) {
    return parseInt(el.getAttribute('data-slide'), 10);
  }

  /**
   * Tabbing through can cause weird positioning, this makes sure active slide
   * is always properly positioned
   * @private
   */
  _onSlideSettle() {
    this._setCellHeights();
    this.flickity.reposition();
  }

  /**
   *
   * @private
   */
  _setCellHeights() {
    if (this.flickity) {
      this.flickity.resize();
    }
  }

  _initFlickity() {
    this.flickity = new Flickity(this.$slideshow[0], {
      accessibility: true,
      adaptiveHeight: true,
      autoPlay: this.autoplay,
      cellAlign: 'left',
      cellSelector: selectors.slideshow.slide,
      friction: 0.8,
      pageDots: false,
      pauseAutoPlayOnHover: true,
      prevNextButtons: false,
      selectedAttraction: 0.2,
      wrapAround: true,
      initialIndex: this.pendingSlideIndex,
    });

    this._setCellHeights();
    this._bindEvents();
  }

  _bindEvents() {
    this.$slides.each((i, slide) => {
      this.events.register(slide, 'focusin', event => {
        event.preventDefault();
        event.stopPropagation();

        const slideIndex = this._getSlideIndex(event.currentTarget);

        // Instant transitions confuse Flickity going back
        // Non-instant transitions confuse Flickity going forward
        const isInstant = slideIndex > this.flickity.selectedIndex;

        this.selectSlide(slideIndex, false, isInstant);
      });
    });

    this.events.register(this.$el[0], 'click', e => {
      if (e.target.matches(selectors.slideshow.paginationNext)) {
        this.flickity.next();
      } else if (e.target.matches(selectors.slideshow.paginationPrev)) {
        this.flickity.previous();
      }
    });

    this.flickity.on('settle', this._onSlideSettle);
  }
}
